/**
 * AGPChart - Ambulatory Glucose Profile Chart for Dashboard (Part 3)
 *
 * Shows glucose percentiles across a typical 24-hour period:
 * - 5th-95th percentile band (light shading)
 * - 25th-75th percentile band (medium shading)
 * - 50th percentile line (median)
 * - Target range overlay
 */

import { Box, Skeleton, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import {
  formatGlucoseForDisplay,
  GLUCOSE_CAP_COLOR,
  GLUCOSE_VALID_RANGE,
  getFixedGlucoseAxis,
  isGlucoseOutOfRange,
  prepareGlucoseChartSeries,
} from '../../common'
import { LazyPlot as Plot } from '../../common/LazyPlot'
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics'
import { useGlucoseDisplay } from '../../hooks/useGlucoseDisplay'
import { AGPData, GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS } from '../../types/dashboard'
import { computeAGPPercentiles } from '../../utils/agpPercentiles'
import { SHARED_METRICS_PER_PAGE } from '../../utils/paginationUtils'

export interface AGPChartProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  height?: number
  targetMin?: number
  targetMax?: number
}

/**
 * Split data into continuous segments (no NaN gaps)
 * Returns array of segments, each with x values and corresponding y values
 */
interface DataSegment {
  x: number[]
  y: number[]
}

const splitIntoSegments = (xValues: number[], yValues: number[]): DataSegment[] => {
  const segments: DataSegment[] = []
  let currentSegment: DataSegment | null = null

  for (let i = 0; i < xValues.length; i++) {
    const y = yValues[i]
    if (!Number.isNaN(y)) {
      if (!currentSegment) {
        currentSegment = { x: [], y: [] }
      }
      currentSegment.x.push(xValues[i])
      currentSegment.y.push(y)
    } else {
      if (currentSegment && currentSegment.x.length > 0) {
        segments.push(currentSegment)
        currentSegment = null
      }
    }
  }

  // Don't forget the last segment
  if (currentSegment && currentSegment.x.length > 0) {
    segments.push(currentSegment)
  }

  return segments
}

export const AGPChart: React.FC<AGPChartProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    height = 350,
    targetMin = GLUCOSE_THRESHOLDS.TARGET_MIN,
    targetMax = GLUCOSE_THRESHOLDS.TARGET_MAX,
  }) => {
    const translate = useTranslate()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const { glucoseUnit, convertValue, formatValue } = useGlucoseDisplay()
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId

    // Keep AGP Y-axis fixed for cross-report comparability.
    const fixedAxis = getFixedGlucoseAxis(glucoseUnit)

    // Use the unified metrics hook that handles virtual devices
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Calculate AGP data
    const agpData = useMemo((): AGPData | null => {
      if (!metricsData || metricsData.length === 0) return null

      // Apply BGM correction + clamping before smoothing so percentile bands
      // align with the corrected target overlay (the standard chart pipeline).
      const filteredMetrics = prepareGlucoseChartSeries(metricsData)
      if (filteredMetrics.length === 0) return null

      // 96 fifteen-minute time-of-day buckets → 5/25/50/75/95th percentiles.
      // Shared with the vet PDF's native AGP (utils/agpPercentiles, vic/vetpo#48).
      const { timeSlots, p5, p25, p50, p75, p95 } = computeAGPPercentiles(filteredMetrics)

      return {
        timeSlots,
        p5,
        p25,
        p50,
        p75,
        p95,
        targetMin,
        targetMax,
      }
    }, [metricsData, targetMin, targetMax])

    if (isLoading) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={height} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        </Box>
      )
    }

    if (!agpData) {
      return (
        <Box sx={{ p: 2, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">
            {translate('pos.dashboard.no_data_available')}
          </Typography>
        </Box>
      )
    }

    // Create x-axis values as hours for better readability
    const xValues = agpData.timeSlots.map((_, i) => i / 4) // Convert to hours

    // Clamp plotted percentile values into the visible axis band so out-of-range
    // highs (and sensor-floor lows) ride the boundary instead of falling off the
    // fixed [0, ceil] axis and leaving a gap in the line (issue1: the AGP line
    // dropped out where the median crossed 30 mmol/L). NaN (empty buckets) passes
    // through so genuine data gaps are preserved. Mirrors GlucoseTrendChart.
    const displayFloor = convertValue(GLUCOSE_VALID_RANGE.min)
    const displayCeil = fixedAxis.max
    const clampToAxis = (converted: number) =>
      Number.isNaN(converted) ? converted : Math.min(Math.max(converted, displayFloor), displayCeil)

    // Convert AGP data arrays to the selected unit, clamped to the axis band.
    const convertedP5 = agpData.p5.map((v) => clampToAxis(convertValue(v)))
    const convertedP25 = agpData.p25.map((v) => clampToAxis(convertValue(v)))
    const convertedP75 = agpData.p75.map((v) => clampToAxis(convertValue(v)))
    const convertedP95 = agpData.p95.map((v) => clampToAxis(convertValue(v)))
    const convertedTargetMin = convertValue(targetMin)
    const convertedTargetMax = convertValue(targetMax)

    // Split data into continuous segments (to properly handle gaps in fill areas)
    // Plotly's fill: 'tonexty' connects across NaN gaps, so we need separate traces per segment
    const segments95 = splitIntoSegments(xValues, convertedP95)
    const segments5 = splitIntoSegments(xValues, convertedP5)
    const segments75 = splitIntoSegments(xValues, convertedP75)
    const segments25 = splitIntoSegments(xValues, convertedP25)

    // Plotly traces
    const traces: Plotly.Data[] = [
      // Target range band (background)
      {
        x: [0, 24],
        y: [convertedTargetMin, convertedTargetMin],
        type: 'scatter',
        mode: 'lines',
        line: { width: 0 },
        showlegend: false,
        hoverinfo: 'skip',
      },
      {
        x: [0, 24],
        y: [convertedTargetMax, convertedTargetMax],
        type: 'scatter',
        mode: 'lines',
        fill: 'tonexty',
        fillcolor: 'rgba(34, 197, 94, 0.1)',
        line: { width: 0 },
        showlegend: false,
        hoverinfo: 'skip',
      },
    ]

    // Add 5th-95th percentile bands for each segment
    // We need to match segments by their x ranges
    segments95.forEach((seg95, i) => {
      // Find matching segment in p5 (same x range)
      const seg5 = segments5[i]
      if (!seg5 || seg95.x.length !== seg5.x.length) return

      // Upper bound (P95)
      traces.push({
        x: seg95.x,
        y: seg95.y,
        type: 'scatter',
        mode: 'lines',
        line: { width: 0 },
        showlegend: false,
        hoverinfo: 'skip',
      })
      // Lower bound with fill (P5)
      traces.push({
        x: seg5.x,
        y: seg5.y,
        type: 'scatter',
        mode: 'lines',
        fill: 'tonexty',
        fillcolor: 'rgba(96, 165, 250, 0.2)',
        line: { width: 0 },
        name: i === 0 ? translate('pos.agp.percentile_5_95') : undefined,
        showlegend: i === 0,
        hoverinfo: 'skip',
      })
    })

    // Add 25th-75th percentile bands for each segment
    segments75.forEach((seg75, i) => {
      const seg25 = segments25[i]
      if (!seg25 || seg75.x.length !== seg25.x.length) return

      // Upper bound (P75)
      traces.push({
        x: seg75.x,
        y: seg75.y,
        type: 'scatter',
        mode: 'lines',
        line: { width: 0 },
        showlegend: false,
        hoverinfo: 'skip',
      })
      // Lower bound with fill (P25)
      traces.push({
        x: seg25.x,
        y: seg25.y,
        type: 'scatter',
        mode: 'lines',
        fill: 'tonexty',
        fillcolor: 'rgba(96, 165, 250, 0.4)',
        line: { width: 0 },
        name: i === 0 ? translate('pos.agp.percentile_25_75') : undefined,
        showlegend: i === 0,
        hoverinfo: 'skip',
      })
    })

    // Add the median line, split into colored sub-segments: blue normally, grey
    // where the median is out of range (clamped to the cap). The hover masks the
    // exact value as "Out of range (>30 mmol/L)" instead of a meaningless number,
    // consistent with the Daily Trend chart and GlucoseTrendChart. NaN buckets
    // break the line so empty periods stay as gaps.
    const MEDIAN_COLOR = '#1976d2'
    const medianSegments: { x: number[]; y: number[]; text: string[]; color: string }[] = []
    {
      let segX: number[] = []
      let segY: number[] = []
      let segText: string[] = []
      let segColor = ''
      const flush = () => {
        if (segX.length > 0) {
          medianSegments.push({ x: [...segX], y: [...segY], text: [...segText], color: segColor })
          segX = []
          segY = []
          segText = []
        }
      }
      for (let i = 0; i < xValues.length; i++) {
        const mmol = agpData.p50[i]
        if (Number.isNaN(mmol)) {
          flush()
          continue
        }
        const oor = isGlucoseOutOfRange(mmol)
        const color = oor ? GLUCOSE_CAP_COLOR : MEDIAN_COLOR
        const plotted = clampToAxis(convertValue(mmol))
        const text = oor
          ? `${translate('pos.glucose.out_of_range')} (${formatGlucoseForDisplay(mmol, glucoseUnit)})`
          : formatGlucoseForDisplay(mmol, glucoseUnit)
        if (color !== segColor && segX.length > 0) {
          // Carry the boundary point into both segments so the color change is gapless.
          segX.push(xValues[i])
          segY.push(plotted)
          segText.push(text)
          flush()
          segX = [xValues[i]]
          segY = [plotted]
          segText = [text]
          segColor = color
        } else {
          segX.push(xValues[i])
          segY.push(plotted)
          segText.push(text)
          if (segX.length === 1) segColor = color
        }
      }
      flush()
    }

    // Attach the legend entry to the first in-range (blue) segment so the swatch
    // shows the median color rather than grey; fall back to the first segment.
    const legendSegIdx = medianSegments.findIndex((s) => s.color === MEDIAN_COLOR)
    const medianLegendIdx = legendSegIdx === -1 ? 0 : legendSegIdx
    medianSegments.forEach((seg, i) => {
      traces.push({
        x: seg.x,
        y: seg.y,
        text: seg.text,
        type: 'scatter',
        mode: 'lines',
        line: { color: seg.color, width: 2.5 },
        name: i === medianLegendIdx ? translate('pos.agp.median') : undefined,
        showlegend: i === medianLegendIdx,
        hovertemplate: `${translate('pos.agp.hover_time')}: %{x:.0f}:00<br>${translate('pos.agp.hover_median')}: %{text}<extra></extra>`,
      })
    })

    // Target range lines (always full width)
    traces.push(
      {
        x: [0, 24],
        y: [convertedTargetMin, convertedTargetMin],
        type: 'scatter',
        mode: 'lines',
        line: { color: TIR_ZONE_COLORS.target, width: 1, dash: 'dash' },
        name: `${translate('pos.glucose.target_low')} (${formatValue(targetMin)})`,
        hoverinfo: 'skip',
      },
      {
        x: [0, 24],
        y: [convertedTargetMax, convertedTargetMax],
        type: 'scatter',
        mode: 'lines',
        line: { color: TIR_ZONE_COLORS.target, width: 1, dash: 'dash' },
        name: `${translate('pos.glucose.target_high')} (${formatValue(targetMax)})`,
        hoverinfo: 'skip',
      },
    )

    const layout: Partial<Plotly.Layout> = {
      height,
      margin: { t: 30, b: 50, l: 50, r: 20 },
      xaxis: {
        title: translate('pos.agp.time_of_day'),
        range: [0, 24],
        fixedrange: true, // Lock X-axis - no zoom/pan allowed
        tickmode: 'array',
        tickvals: [0, 3, 6, 9, 12, 15, 18, 21, 24],
        ticktext: ['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00', '24:00'],
        showgrid: true,
        gridcolor: '#e0e0e0',
      },
      yaxis: {
        title: glucoseUnit,
        range: [fixedAxis.min, fixedAxis.max],
        fixedrange: true, // Lock Y-axis - no zoom/pan allowed
        showgrid: true,
        gridcolor: '#e0e0e0',
        tickmode: 'array',
        tickvals: fixedAxis.tickValues,
        ticktext: fixedAxis.tickLabels,
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        y: -0.15,
        x: 0.5,
        xanchor: 'center',
      },
      hovermode: 'x unified',
      dragmode: false, // Disable all drag interactions (pan, zoom box, etc.)
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
    }

    return (
      <Box>
        <Plot
          data={traces}
          layout={layout}
          config={{
            displayModeBar: false, // Hide toolbar completely for standardized view
            displaylogo: false,
            staticPlot: false, // Allow hover tooltips but no zoom/pan
            scrollZoom: false, // Disable scroll wheel zoom
            doubleClick: false, // Disable double-click to zoom
            responsive: true,
          }}
          style={{ width: '100%' }}
        />
      </Box>
    )
  },
)

export default AGPChart
